import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setEditableMessage } from '../../actions/chat/actions';
import { BLOB_ID } from '../../constants/blobId';
import { currentOpenChatSelector } from '../../selectors';
import { setIsReplyTemplateInputVisible } from '../../slices/replyTemplatesSlice';

export const MAX_MESSAGE_LENGTH = 2000;

type TUseMessageInputParams = {
  chatId: string;
  isSendButtonDisabled?: boolean;
  onSend(params: { text: string; chatId: string; captchaToken?: string | null; payload?: string }): void;
  onHeightChange(): void;
};

export const useMessageInput = ({ chatId, isSendButtonDisabled, onSend, onHeightChange }: TUseMessageInputParams) => {
  const [text, setText] = React.useState('');

  // FIXME: избавиться от useSelector и dispatch в хуке компонента
  const dispatch = useDispatch();

  const currentChat = useSelector(currentOpenChatSelector);

  React.useEffect(() => {
    if (currentChat?.editableMessage) {
      setText(currentChat.editableMessage);
    } else {
      setText('');
    }
  }, [chatId, currentChat?.editableMessage]);

  React.useEffect(() => {
    if (text) {
      dispatch(setIsReplyTemplateInputVisible(false));
    }
  }, [dispatch, text]);

  const onBlur = React.useCallback(() => {
    dispatch(setEditableMessage({ chatId, text, payload: currentChat?.defaultMessagePayload }));
    if (!text) {
      dispatch(setIsReplyTemplateInputVisible(true));
    }
  }, [chatId, dispatch, text, currentChat]);

  const onFocus = React.useCallback(() => {
    dispatch(setIsReplyTemplateInputVisible(false));
  }, [dispatch]);

  const onValueChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;

    setText(text.slice(0, MAX_MESSAGE_LENGTH));
    onHeightChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trySend = React.useCallback(() => {
    if (text.trim().length) {
      onSend({
        text,
        chatId,
        payload: currentChat?.defaultMessagePayload,
      });

      if (chatId !== BLOB_ID) {
        setText('');
        dispatch(setEditableMessage({ chatId, text: '' }));
      }
    }
  }, [text, onSend, chatId, dispatch, currentChat]);

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          return;
        }

        if (isSendButtonDisabled) {
          event.preventDefault();

          return;
        }

        event.preventDefault();
        trySend();
      }
    },
    [trySend, isSendButtonDisabled],
  );

  return React.useMemo(
    () => ({
      text,
      onKeyDown,
      onValueChange,
      onBlur,
      onFocus,
      trySend,
    }),
    [text, onKeyDown, onValueChange, onBlur, onFocus, trySend],
  );
};
